import React, { useEffect, useState } from 'react';
import {
  Create, SimpleForm, SelectInput, AutocompleteInput, required, useTranslate, useNotify, useRedirect,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Grid, Stack } from '@mui/material';
import { httpClient, HttpError } from '@/utils/httpClient';
import { DOCUMENT_SIDE_TO_INT } from '@/config/constants';
import { FormPageTitle, FormSection, FormToolbar } from '@/components/shell';
import { MappingFieldsEditor, MappingHelp } from './MappingFieldsEditor';
import { BothPreviewWatcher } from './BothPreviewPanel';
import { useDocumentSideChoices } from './documentSides';
import { PriorityResolutionDialog } from './PriorityResolutionDialog';
import type {
  BothCompositionConflictResponse,
  FieldConflict,
  PrioritizationOverrides,
} from './bothComposition.types';

const BACK_TO = '/kyc-config?tab=fields-mapping';

/** Filters the Document Type select to exclude combos already mapped for the chosen nationality+side. */
const DocumentInfoFields = ({
  documentTypes, nationalities, existingCombos, t,
}: {
  documentTypes: any[]; nationalities: any[];
  existingCombos: Array<{ docTypeId: number; nationalityId: number; side: number }>;
  t: (k: string, o?: any) => string;
}) => {
  // Operators cannot create the auto-generated `Both` row by hand.
  const sideChoices = useDocumentSideChoices(true);
  const nationalityId = useWatch({ name: 'nationalityId' });
  const documentSide = useWatch({ name: 'documentSide' });
  const sideInt = DOCUMENT_SIDE_TO_INT[documentSide];

  const availableDocTypes = nationalityId
    ? documentTypes.filter(d => !existingCombos.some(c =>
        c.nationalityId === nationalityId && c.docTypeId === d.id &&
        (sideInt === undefined || c.side === sideInt)
      ))
    : documentTypes;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <AutocompleteInput source="nationalityId" label={t('screens.fieldMappings.nationality')}
          choices={nationalities.map(n => ({ id: n.id, name: n.name }))}
          validate={required()} fullWidth />
      </Grid>
      <Grid item xs={12} md={4}>
        <AutocompleteInput source="documentTypeId" label={t('screens.fieldMappings.documentType')}
          choices={availableDocTypes.map(d => ({ id: d.id, name: d.name }))}
          validate={required()} fullWidth
          helperText={nationalityId ? undefined : t('screens.fieldMappings.pickNationalityFirst')}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SelectInput source="documentSide" label={t('screens.fieldMappings.documentSide')}
          choices={sideChoices} validate={required()} fullWidth
          helperText={t('fields-mapping.both.create_helper')}
        />
      </Grid>
    </Grid>
  );
};

export const FieldMappingCreate = () => {
  const t = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [nationalities, setNationalities] = useState<any[]>([]);
  const [kycFields, setKycFields] = useState<any[]>([]);
  const [standardFields, setStandardFields] = useState<any[]>([]);
  const [existingCombos, setExistingCombos] = useState<Array<{ docTypeId: number; nationalityId: number; side: number }>>([]);
  const [mappingFields, setMappingFields] = useState<any[]>([
    { standardFieldName: '', regex: '', kycMappingField: [{ prefix: '', kycField: { id: 0, name: '' }, suffix: '' }] },
  ]);
  const [pendingConflict, setPendingConflict] = useState<{ data: any; conflicts: FieldConflict[] } | null>(null);

  useEffect(() => {
    httpClient.get<any[]>('/admin/document-type/GetAllDocumentTypes').then(r => setDocumentTypes(r.data));
    httpClient.get<any[]>('/admin/nationality/GetAllNationality').then(r => setNationalities(r.data));
    httpClient.get<any[]>('/admin/kyc-field/GetAllKYCFields').then(r => setKycFields(r.data));
    httpClient.get<any[]>('/admin/fields-mapping/GetStandardFields').then(r => setStandardFields(r.data));
    // Fetch existing mappings to filter out already-used (nationality, docType, side) combos
    httpClient.get<any>('/admin/fields-mapping/GetMappingFields?draw=1&length=10000&start=0&search=&column=&orderBy=asc')
      .then(r => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        setExistingCombos(rows.map((row: any) => ({
          docTypeId: row.documentTypeResponse?.id ?? row.documentTypeId,
          nationalityId: row.nationalityResponse?.id ?? row.nationalityId,
          side: typeof row.documentSide === 'number' ? row.documentSide : DOCUMENT_SIDE_TO_INT[row.documentSide],
        })).filter((c: any) => c.docTypeId && c.nationalityId));
      })
      .catch(() => { /* non-blocking */ });
  }, []);

  const kycFieldNameById = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const k of kycFields) m[k.id] = k.name;
    return m;
  }, [kycFields]);

  const buildPayload = (data: any, overrides?: PrioritizationOverrides) => ({
    documentTypeId: data.documentTypeId,
    nationalityId: data.nationalityId,
    documentSide: DOCUMENT_SIDE_TO_INT[data.documentSide] ?? 1,
    mappingFields: mappingFields.map(f => ({
      fieldName: f.standardFieldName || '',
      regex: f.regex || '',
      kycMappingField: (f.kycMappingField || []).map((k: any) => ({
        prefix: k.prefix || '',
        kycFieldId: k.kycField?.id || null,
        suffix: k.suffix || '',
      })),
    })),
    ...(overrides ? { prioritizationOverrides: overrides } : {}),
  });

  const submit = async (data: any, overrides?: PrioritizationOverrides) => {
    const payload = buildPayload(data, overrides);
    try {
      await httpClient.post('/admin/fields-mapping/AddFieldMapping', payload);
      notify('fields-mapping.both.save_success', { type: 'success' });
      redirect(BACK_TO);
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 409) {
          const body = err.data as BothCompositionConflictResponse;
          setPendingConflict({ data, conflicts: body?.conflicts || [] });
          return;
        }
        if (err.status === 400) {
          const msg = (err.data as { message?: string })?.message || err.message;
          notify(msg, { type: 'error' });
          return;
        }
      }
      notify(err instanceof Error ? err.message : 'ra.notification.http_error', { type: 'error' });
    }
  };

  return (
    <>
      <Create redirect={false} actions={<FormPageTitle mode="create" />}>
        <SimpleForm onSubmit={(data) => submit(data)} toolbar={<FormToolbar backTo={BACK_TO} alwaysEnableSave />}>
          <Stack spacing={3} sx={{ width: '100%' }}>
            <FormSection title={t('screens.fieldMappings.documentInfo')} action={<MappingHelp />}>
              <DocumentInfoFields
                documentTypes={documentTypes}
                nationalities={nationalities}
                existingCombos={existingCombos}
                t={t}
              />
            </FormSection>
            <FormSection title={t('screens.fieldMappings.mappingFields')}>
              <MappingFieldsEditor
                mappingFields={mappingFields}
                onChange={setMappingFields}
                kycFields={kycFields}
                standardFields={standardFields}
              />
              <BothPreviewWatcher
                mappingFields={mappingFields}
                kycFieldNameById={kycFieldNameById}
                documentTypeSource="documentTypeId"
                nationalitySource="nationalityId"
              />
            </FormSection>
          </Stack>
        </SimpleForm>
      </Create>
      <PriorityResolutionDialog
        open={!!pendingConflict}
        conflicts={pendingConflict?.conflicts || []}
        kycFieldNameById={kycFieldNameById}
        onCancel={() => setPendingConflict(null)}
        onConfirm={(overrides) => {
          const pending = pendingConflict;
          setPendingConflict(null);
          if (pending) submit(pending.data, overrides);
        }}
      />
    </>
  );
};
